import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useCreateTypeCertificationMutation,
  useDeleteTypeCertificationMutation,
  useGetMasterListByUrlQuery,
  useGetTypeCertificationsQuery,
} from 'app/services/ahuComponentFormsApi';
import { COMPONENT_FORM_CONFIGS } from '../componentFormConfigs';
import { useComponentForm } from '../useComponentForm';
import { FormSubtab, handleTabToNext, type OptionRow } from '../formKit';
import {
  BlankOffSlot,
  FormHeading,
  MBareInput,
  MBareSelect,
  MInput,
  MSelect,
  PBareSelect,
  PricingRows,
  PSelect,
  RequiredFieldNote,
} from './formCommon';
import type { ComponentFormProps } from './ComponentFormHost';

/**
 * Phase 6e2a — ENERGY WHEEL form (§50.1 row; partName er_wheel, classID
 * component.er_wheel.model — the Q22 naming split; legacy form.js 180 +
 * form.tmpl 1,576; parity 325):
 *  - dual-section (sections:[a,b] — section2 = bound partner);
 *  - speeds additionalLoadable gated on mfg+series;
 *  - certifications multiCheckbox → ahu/energy_wheels/<id>/certifications/
 *    (commit-on-close diff — §49.5; syncdata ahuenergywheelcertification);
 *  - flowTypes/fanLocations master collections ([NEG] cache-check bug is a
 *    stateStorage artifact — RTKQ caching replaces it; observable identical:
 *    lists always fresh);
 *  - er_wheel re-render → redraw:canvas (chassis);
 *  - 4-way winter/summer ESP diffing (chassis espInfoFields).
 */
const config = COMPONENT_FORM_CONFIGS.energy_wheel;

const ESP_FIELDS = [
  'sa_apd_summer',
  'ea_apd_summer',
  'sa_apd_winter',
  'ea_apd_winter',
  'sa_air_stream_summer_id',
  'ea_air_stream_summer_id',
  'sa_air_stream_winter_id',
  'ea_air_stream_winter_id',
];

/**
 * One season sub-section: a `li.sublist-heading.two-headings` (legacy
 * form.tmpl:443 / 713 / 1018 / 1220) + the dual-COLUMN rows where the ENTERING
 * side (`a` = oa|ra) is the left input and the LEAVING side (`b` = sa|ea) is the
 * right input, ON ONE LINE per metric. Replaces the prior linear `label=""`
 * pairs — the 6e2a parked MAJOR (the season-block dual-column layout).
 *
 * - DB/WB is a single line with a `/` separator (legacy gas-heater-total-airflow
 *   row): oa_db / oa_wb  then  sa_db / sa_wb.
 * - Humidity is %; Humidity Ratio is gr/lb. The summer tmpl uses rh→Humidity,
 *   hr→Humidity Ratio; winter swaps (hr first). (Matches the original code's
 *   per-season name swap.)
 * - Face Velocity + APD are on the LEAVING (`b`) side only ("right-column").
 *
 * ★ PARITY — legacy copy-pasted this block FOUR times (summer SA :443-910,
 * summer EA :713-910, winter SA :1018-1213, winter EA :1220-1415) and the four
 * copies drifted. The port collapsed them onto the summer-SA markup, so three
 * of the four rendered with the wrong wrapper classes and the wrong input
 * widths. `ahu/styles.css` keys real geometry off exactly those classes —
 * `.first-two-line.er-wheel select:nth-of-type(2){margin-left:20px}` (:5257),
 * `.er_wheel-form .inputs-group li > div > input.xsmall-input{width:50px}`
 * (:5556), `.medium-input{max-width:113px}` (:5559) — so this is not cosmetic
 * naming. The per-copy differences, verbatim from the template:
 *
 *              air-stream wrapper      SP row          DB/WB      Humidity   FaceVel/APD
 *   summer SA  single-line er-wheel    oa_sp decimal   xsmall     unit       multiply-total
 *   summer EA  first-two-line er-wheel ra_sp INTEGER   xsmall     unit       multiply-total
 *   winter SA  first-two-line er-wheel oa_sp, li.left- xsmall+int short-unit (no class)
 *   winter EA  first-two-line er-wheel NONE            xsmall+int unit       multiply-total
 *
 * and only summer SA omits the leading `CFM` unit span on the Air Stream row
 * (tmpl:448-450 has it commented out; the other three ship it). The winter EA
 * Airflow row is a `multiply-total` where the other three are `first-two-line`
 * (tmpl:1246). Legacy's own oddities, reproduced rather than tidied — tidying
 * them is what produced the report.
 *
 * NOT reproduced, because they are legacy DATA bugs rather than styling: the
 * summer EA leaving-side humidity inputs are named `ea_hr_summer` /
 * `ea_rh_summer` the wrong way round (tmpl:825 / :848) and the winter EA one is
 * spelt `rea_hr_winter` (tmpl:1331) — a field that saves nowhere. The port's
 * corrected names stand.
 */
const StreamSubsection = ({
  api,
  a,
  b,
  season,
  streams,
  enteringHeading,
  leavingHeading,
  spField,
  spLabel,
}: {
  api: ReturnType<typeof useComponentForm>;
  a: string;
  b: string;
  season: 'summer' | 'winter';
  streams: OptionRow[];
  enteringHeading: string;
  leavingHeading: string;
  spField?: string;
  spLabel?: string;
}) => {
  // per-season humidity / humidity-ratio field names (legacy swap).
  const humField = (pre: string) => (season === 'winter' ? `${pre}_hr_${season}` : `${pre}_rh_${season}`);
  const hrField = (pre: string) => (season === 'winter' ? `${pre}_rh_${season}` : `${pre}_hr_${season}`);
  const isWinter = season === 'winter';
  const isEa = b === 'ea';
  // tmpl:448 — ONLY the summer SA copy is `single-line er-wheel` with the CFM
  // span commented out; :718 / :1023 / :1225 are `first-two-line er-wheel` and
  // ship the span. `.first-two-line.er-wheel` is what gives the second select
  // its 20px gutter (ahu/styles.css:5257).
  const summerSa = !isWinter && !isEa;
  // tmpl:1246 — the winter EA Airflow row alone is a `multiply-total`.
  const airflowClass = isWinter && isEa ? 'multiply-total' : 'first-two-line';
  // tmpl:1112 etc — the winter copies add `integer` to all four DB/WB boxes.
  const tempClass = (rest: string) => `xsmall-input${isWinter ? ' integer' : ''}${rest}`;
  // tmpl:1156 — winter SA alone narrows the `%` unit with `short-unit`.
  const humUnitClass = isWinter && !isEa ? 'short-unit unit' : 'unit';
  // tmpl:1180/:1197 — winter SA alone leaves Face Velocity / APD unwrapped.
  const totalClass = isWinter && !isEa ? undefined : 'multiply-total';
  // tmpl:511 oa_sp is `decimal`, :781 ra_sp is `integer` — legacy's own split.
  const spFormatter = spField === 'ra_sp' ? 'integer' : 'decimal';
  return (
    <>
      <li className="sublist-heading two-headings">
        <h3>{enteringHeading} </h3>
        <h3>{leavingHeading}</h3>
      </li>
      {/* Air Stream (entering | leaving) */}
      <li>
        <div className={summerSa ? 'single-line er-wheel' : 'first-two-line er-wheel'}>
          {!summerSa && <><span className="unit">CFM</span>{' '}</>}
          <MBareSelect api={api} name={`${a}_air_stream_${season}_id`} options={streams} extraClass="medium-input" />{' '}
          <MBareSelect api={api} name={`${b}_air_stream_${season}_id`} options={streams} extraClass="medium-input right-input" />{' '}
          <span className="label">Air Stream</span>
        </div>
      </li>
      {/* Airflow (entering | leaving) */}
      <li>
        <div className={airflowClass}>
          <span className="unit">CFM</span>{' '}
          <MBareInput api={api} name={`${a}_airflow_actual_${season}`} formatter="integer" extraClass="medium-input integer" />{' '}
          <MBareInput api={api} name={`${b}_airflow_actual_${season}`} formatter="integer" extraClass="medium-input integer" />{' '}
          <span className="label">Airflow</span>
        </div>
      </li>
      {/* SP Outside / SP Return (single, on the entering side). The winter EA
          copy has NO SP row at all (tmpl:1240-1260 goes Airflow → DB/WB), which
          is why the caller passes no spField for it; the winter SA li carries
          `left-column` (tmpl:1078). */}
      {spField && (
        <li className={isWinter ? 'left-column' : undefined}>
          <div className="first-two-line">
            <span className="unit">in WC</span>{' '}
            <MBareInput api={api} name={spField} formatter={spFormatter} extraClass="medium-input integer" />{' '}
            <span className="label">{spLabel}</span>
          </div>
        </li>
      )}
      {/* DB | WB (entering db/wb, leaving db/wb on one line) */}
      <li>
        <div className="gas-heater-total-airflow">
          <span className="unit temp"> F </span>{' '}
          <MBareInput api={api} name={`${a}_db_temp_${season}`} extraClass={tempClass(' mr-1')} />{' '}
          <span className="multiply-sign" tabIndex={-1}>/</span>{' '}
          <MBareInput api={api} name={`${a}_wb_temp_${season}`} extraClass={tempClass(' mr-20')} />{' '}
          <MBareInput api={api} name={`${b}_db_temp_${season}`} extraClass={tempClass(' mr-1')} />{' '}
          <span className="multiply-sign">/</span>{' '}
          <MBareInput api={api} name={`${b}_wb_temp_${season}`} extraClass={tempClass('')} />{' '}
          <span className="label">
            <span>DB</span> / <span>WB</span>
          </span>
        </div>
      </li>
      {/* Humidity (%) */}
      <li>
        <div>
          <span className={humUnitClass}>%</span>{' '}
          <MBareInput api={api} name={humField(a)} extraClass="medium-input integer" />{' '}
          <MBareInput api={api} name={humField(b)} extraClass="medium-input integer" />{' '}
          <span className="label">Humidity</span>
        </div>
      </li>
      {/* Humidity Ratio (gr/lb) */}
      <li>
        <div>
          <span className="unit">gr/lb</span>{' '}
          <MBareInput api={api} name={hrField(a)} extraClass="medium-input integer" />{' '}
          <MBareInput api={api} name={hrField(b)} extraClass="medium-input integer" />{' '}
          <span className="label">Humidity Ratio</span>
        </div>
      </li>
      {/* Enthalpy (btu/lb) */}
      <li>
        <div>
          <span className="unit">btu/lb</span>{' '}
          <MBareInput api={api} name={`${a}_w_${season}`} extraClass="medium-input integer" />{' '}
          <MBareInput api={api} name={`${b}_w_${season}`} extraClass="medium-input integer" />{' '}
          <span className="label">Enthalpy</span>
        </div>
      </li>
      {/* Face Velocity (leaving side, readonly) */}
      <li className="right-column">
        <div className={totalClass}>
          <span className="unit">fpm</span>{' '}
          <MBareInput api={api} name={`${b}_face_velocity_${season}`} formatter="integer" extraClass="medium-input integer" readOnly />{' '}
          <span className="label">Face Velocity</span>
        </div>
      </li>
      {/* APD (leaving side) */}
      <li className="right-column">
        <div className={totalClass}>
          <span className="unit"><span>in</span> <span>WC</span></span>{' '}
          <MBareInput api={api} name={`${b}_apd_${season}`} extraClass="medium-input integer" />{' '}
          <span className="label">APD</span>
        </div>
      </li>
      {/* Condensate — winter EA only, and the last row of that block
          (tmpl:1402-1415). It is a `right-column` / `multiply-total` row like
          the two above it, not the plain MInput row the port used. */}
      {isWinter && isEa && (
        <li className="right-column">
          <div className="multiply-total">
            <span className="unit">gpm</span>{' '}
            <MBareInput api={api} name="condensate_winter" extraClass="medium-input integer" />{' '}
            <span className="label">Condensate</span>
          </div>
        </li>
      )}
    </>
  );
};

/**
 * The efficiency / capacity block that closes each season (legacy
 * form.tmpl:913-1006 summer, :1416-1524 winter).
 *
 * ★ PARITY — legacy puts the PERCENTAGE and the btu/hr CAPACITY of Sensible,
 * Latent and Total on ONE row, under one label, inside a `.multiply-total`
 * (which is what `.er_wheel-form … .medium-input.ml-3{margin-left:3px}`,
 * ahu/styles.css:5565, spaces). The port emitted eight separate `MInput` rows —
 * three of them with an EMPTY label — so the block ran twice as tall as dev's
 * and every box was a full-width `.model_field` instead of a 113px-capped
 * `.medium-input`. It also dropped the empty `li.sublist-heading.two-headings`
 * that opens the block (tmpl:913 / :1416) and is the rule above "Unit
 * Effectiveness" on dev.
 *
 * Summer and winter differ again, and again only in wrappers: summer's first
 * two rows are bare `<div>`s with a `short-unit` on Unit Effectiveness
 * (tmpl:916-920), winter's are `.multiply-total` with a plain `unit`
 * (tmpl:1418-1422); summer's Total EFFICIENCY box carries a stray extra `ml-3`
 * (tmpl:991) that winter's does not. EATR / OACF are winter-only and carry no
 * unit span at all (tmpl:1508-1523).
 */
/** one `% efficiency | btu/hr capacity` row (tmpl:938-960 / :1466-1488). */
const TotalsPair = ({ api, season, field, label, effMl3 }: {
  api: ReturnType<typeof useComponentForm>;
  season: 'summer' | 'winter';
  field: string;
  label: string;
  effMl3?: boolean;
}) => (
  <li>
    <div className="multiply-total">
      <span className="short-unit unit">%</span>{' '}
      <MBareInput api={api} name={`${field}_efficiency_${season}`}
        extraClass={`medium-input integer${effMl3 ? ' ml-3' : ''}`} />{' '}
      <span className="unit">btu/hr</span>{' '}
      <MBareInput api={api} name={`${field}_capacity_${season}`} formatter="integer"
        extraClass="medium-input integer ml-3" />{' '}
      <span className="label">{label}</span>
    </div>
  </li>
);

const SeasonTotals = ({
  api,
  season,
}: {
  api: ReturnType<typeof useComponentForm>;
  season: 'summer' | 'winter';
}) => {
  const isWinter = season === 'winter';
  // summer :916 `<div class="">` vs winter :1418 `<div class="multiply-total">`
  const headClass = isWinter ? 'multiply-total' : undefined;
  return (
    <>
      <li className="sublist-heading two-headings" />
      <li>
        <div className={headClass}>
          <span className={isWinter ? 'unit' : 'short-unit unit'}>%</span>{' '}
          <MBareInput api={api} name={`unit_effectiveness_${season}`} extraClass="integer" />{' '}
          <span className="label">Unit Effectiveness</span>
        </div>
      </li>
      <li>
        <div className={headClass}>
          <span className="unit">btu/W</span>{' '}
          <MBareInput api={api} name={`rer_${season}`} extraClass="integer" />{' '}
          <span className="label">RER total</span>
        </div>
      </li>
      <TotalsPair api={api} season={season} field="sensible" label="Sensible" />
      <TotalsPair api={api} season={season} field="latent" label="Latent" />
      <TotalsPair api={api} season={season} field="total" label="Total" effMl3={!isWinter} />
      {isWinter && (
        <>
          <li>
            <div className="multiply-total">
              <MBareInput api={api} name="eatr_winter" extraClass="integer" readOnly />{' '}
              <span className="label">EATR</span>
            </div>
          </li>
          <li>
            <div className="multiply-total">
              <MBareInput api={api} name="oacf_winter" extraClass="integer" readOnly />{' '}
              <span className="label">OACF</span>
            </div>
          </li>
        </>
      )}
    </>
  );
};

const EnergyWheelForm = ({ row, section, section2, cms }: ComponentFormProps) => {
  const api = useComponentForm({
    config,
    row,
    section,
    section2,
    firstSubtab: 'properties',
    espInfoFields: ESP_FIELDS,
  });
  const { model } = api;

  // speeds additionalLoadable gated mfg+series (parity 325).
  const engine = api.catalogueFields.master_energy_wheel_id;
  const mfgId = engine?.paramValue('mfg_id');
  const seriesId = engine?.paramValue('series_id');
  const gate = mfgId != null && seriesId != null;
  const { data: speeds } = useGetMasterListByUrlQuery(
    `/api/v1.0/cms/1/v1.0.0/ahu/energy_wheels/params/speed/?mfg_id=${mfgId}&series_id=${seriesId}`,
    { skip: !gate }
  );
  const { data: flowTypes } = useGetMasterListByUrlQuery(
    '/api/v1.0/master/v1.0.0/ahu/energy_wheels/flow_types/'
  );
  const { data: fanLocations } = useGetMasterListByUrlQuery(
    '/api/v1.0/master/v1.0.0/ahu/energy_wheels/fan_locations/'
  );

  // ---- certifications multiCheckbox (§49.5/parity 341) ----
  // ★ The legacy widget is SelectorMultiCheckbox (multi_checkbox_selector.js),
  // wired by form.js:114-129. Three behaviours the port had dropped:
  //  (a) the activator button toggles `.active` on `.certification-edit-list`
  //      (js:132-138) — without that class the list is `max-height:0;
  //      overflow:hidden` (ahu/styles.css:2878/:2915), so the port's list could
  //      never be opened at all and the whole input was inert;
  //  (b) the commit is on the LIST closing — the button again, or a click
  //      elsewhere in the form (js:140-149) — not on the Properties subtab
  //      collapsing, which is what the port keyed off;
  //  (c) `saveSelection` (js:166-193) diffs only inputs carrying `.changed`
  //      against the saved collection, then the collection's own add/remove
  //      drives the output (js:98-100) — so the echo tracks what is SAVED, and
  //      does not move while the list is open.
  const { data: savedCerts, refetch: refetchCerts } = useGetTypeCertificationsQuery({
    plural: 'energy_wheels',
    id: row.id,
  });
  const [createCert] = useCreateTypeCertificationMutation();
  const [deleteCert] = useDeleteTypeCertificationMutation();
  const [certListOpen, setCertListOpen] = useState(false);
  const certGroupRef = useRef<HTMLLIElement>(null);

  const certCommit = useCallback(() => {
    setCertListOpen(false);
    const el = certGroupRef.current;
    if (!el) return;
    const saved = new Set((savedCerts ?? []).map((c) => Number(c.id)));
    const writes: Promise<unknown>[] = [];
    el.querySelectorAll<HTMLInputElement>('input.certification.changed').forEach(
      (input) => {
        input.classList.remove('changed');
        const certId = Number(input.value);
        if (input.checked) {
          if (!saved.has(certId))
            writes.push(createCert({ plural: 'energy_wheels', id: row.id, certId }).unwrap());
        } else if (saved.has(certId)) {
          writes.push(deleteCert({ plural: 'energy_wheels', id: row.id, certId }).unwrap());
        }
      }
    );
    // The endpoint carries no cache tag, so the echo is refreshed by hand —
    // legacy gets this from the Backbone collection mutating in place.
    if (writes.length) void Promise.allSettled(writes).then(() => void refetchCerts());
  }, [savedCerts, createCert, deleteCert, refetchCerts, row.id]);

  // Legacy binds the outside-click on the VIEW element (js:140-149), i.e. the
  // form; document level is the same thing from inside the Data Bar and also
  // commits when the click lands on the board.
  useEffect(() => {
    if (!certListOpen) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (certGroupRef.current && !certGroupRef.current.contains(e.target as Node))
        certCommit();
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [certListOpen, certCommit]);

  const savedCertIds = new Set((savedCerts ?? []).map((c) => Number(c.id)));
  const certKey = [...savedCertIds].sort((a, b) => a - b).join(',');

  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const sub = (name: string) => !!api.subtabs[name];
  const streams = ((cms as Record<string, unknown> | undefined)?.air as
    | { air_stream?: OptionRow[] }
    | undefined)?.air_stream ?? [];

  return (
    <>
      <FormHeading
        title="ER Wheel"
        api={api}
        wrapperClass="component-form-heading clearfix hide"
        chrome={[
          {
            field: config.catalogueFields[0],
            visClass: 'vis-energy_wheel',
            buttonExtraClass: 'hide',
            showSmartlist: false,
            searchResultClass: 'search-result',
          },
        ]}
        withRatingBlock
        ratingBlockDataName="energy_wheel"
      />
      {/* ★ PARITY — `.er_wheel-form` belongs on the FORM, not on the `ul`
          (legacy form.tmpl:13-15 is `<form class="er_wheel-form">` wrapping
          `<ul class="inputs-group" id="er_wheel-form">`). Seven of this form's
          nine geometry rules are written `.er_wheel-form .inputs-group …`
          (ahu/styles.css:5562-5581) and so need `.inputs-group` to be a
          DESCENDANT of `.er_wheel-form`; with both classes on one element they
          match nothing. `input.xsmall-input{width:50px}` is one of them, and
          without it the four DB/WB boxes fall back to the 52px base
          (:4397-4400) — 8px more than the row has, so the row wraps. */}
      <form className="er_wheel-form" onKeyDown={handleTabToNext}>
        {/* id + the legacy #er_wheel-form disable CSS (ahu/styles.css:4904-4956)
            = the rating-mode field lock; its rules strictly cover the
            input[id^=energy_wheel_].rating-mode sibling hook (:4884-4888). */}
        <ul id="er_wheel-form" className="inputs-group">
          <FormSubtab type="energy_wheel" cid={row.id} name="properties" label="Heat Exchanger" labelHidden open={sub('properties')} onToggle={api.toggleSubtab}>
            <PSelect api={api} target="master_energy_wheel_id" name="mfg_id" label="Manufacturer" cmsPath="manufacturer" />
            <PSelect api={api} target="master_energy_wheel_id" name="series_id" label="Series" cmsPath="series" labelKey="series" />
            <li>
              <div>
                <span className="selection">
                  <span>{String(model.get('model') ?? '')}</span>
                </span>
                <select name="model" className="energy_wheel_params_field single-input" required value="None" onChange={() => undefined}>
                  <option value="None">- -</option>
                </select>
                <span className="label">Model</span>
              </div>
            </li>
            <PSelect api={api} target="master_energy_wheel_id" name="type_id" label="Type" cmsPath="type" />
            <li>
              <div className="">
                <span className="unit">in</span>
                <PBareSelect api={api} target="master_energy_wheel_id" name="wheel_size_id" cmsPath="wheel_size" labelKey="value" extraClass="medium-input" />
                <span className="unit">deg</span>
                <PBareSelect api={api} target="master_energy_wheel_id" name="purge_angle_id" cmsPath="purge_angle" labelKey="value" extraClass="medium-input" />
                <span className="label">
                  <span>Wheel Size</span> | <span>Purge</span>
                </span>
              </div>
            </li>
            <PSelect api={api} target="master_energy_wheel_id" name="fin_pattern_id" label="Fin Pattern" cmsPath="fin_pattern" />
            <MSelect api={api} name="flow_type_id" label="Flow" options={(flowTypes ?? []) as OptionRow[]} />
            <li>
              <div className="er-multiply">
                <PBareSelect api={api} target="master_energy_wheel_id" name="mounting_arr_id" cmsPath="mounting_arr" extraClass="medium-input" />
                <MBareSelect api={api} name="fan_locations_id" options={(fanLocations ?? []) as OptionRow[]} extraClass="medium-input" />
                <span className="label">
                  <span>Mounting</span> | <span>Fan Locations</span>
                </span>
              </div>
            </li>
            <li>
              <div className="">
                <span className="unit">RPM</span>
                <MBareSelect
                  api={api}
                  name="speed"
                  options={((speeds ?? []) as unknown[]).map((v) =>
                    v != null && typeof v === 'object'
                      ? (v as OptionRow)
                      : ({ id: v, name: String(v) } as unknown as OptionRow)
                  )}
                  extraClass="medium-input"
                />
                <PBareSelect api={api} target="master_energy_wheel_id" name="speed_control_id" cmsPath="speed_control" extraClass="medium-input" />
                <span className="label">
                  <span>Speed</span> | <span>Control</span>
                </span>
              </div>
            </li>
            <li>
              <div className="">
                <span className="unit">V/fr/ph</span>
                <PBareSelect api={api} target="master_energy_wheel_id" name="mtr_voltage_id" cmsPath="mtr_voltage" extraClass="medium-input" />
                <span className="unit">A</span>
                <input
                  type="text"
                  name="mtr_fla_id"
                  data-formatter_type="decimal"
                  className="energy_wheel_params_field medium-input"
                  value={String(engine?.paramValue('mtr_fla') ?? '')}
                  readOnly
                  tabIndex={-1}
                  onChange={() => undefined}
                />
                <span className="label">
                  <span>Motor Voltage</span> | <span>FLA</span>
                </span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <input
                  type="checkbox"
                  name="frost_control_winter"
                  className="energy_wheel_params_field"
                  checked={!!engine?.paramValue('frost_control_winter')}
                  onChange={(e) =>
                    api.commitParamsField(
                      'master_energy_wheel_id',
                      'frost_control_winter',
                      e.target.checked ? 'true' : 'None'
                    )
                  }
                />
                <span className="label">Frost Control</span>
              </div>
            </li>
            <PSelect api={api} target="master_energy_wheel_id" name="temp_control_id" label="Temperature Control" cmsPath="temp_control" />
            <PSelect api={api} target="master_energy_wheel_id" name="rotation_detector_id" label="Rotation Detector" cmsPath="rotation_detector" />
            <PSelect api={api} target="master_energy_wheel_id" name="coating_frame_id" label="Frame Coating" cmsPath="coating_frame" />
            <PSelect api={api} target="master_energy_wheel_id" name="coating_media_id" label="Media Coating" cmsPath="coating_media" />
            {/* certifications multiCheckbox (commit-on-close). */}
            <li className="certification-group" ref={certGroupRef}>
              <div>
                <button
                  id="show_er_wheel_certifications"
                  type="button"
                  className="icon-selection"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (certListOpen) certCommit();
                    else setCertListOpen(true);
                  }}
                />
                <div className={`certification-edit-list${certListOpen ? ' active' : ''}`}>
                  {/* ★ Uncontrolled + `.changed`, exactly as legacy: the inputs
                      are the pending state until the list closes (js:150-162,
                      :166-193). `certKey` re-baselines `defaultChecked` when
                      the saved set changes (first load, or after a commit). */}
                  <ul key={certKey}>
                    {(((api.componentCms?.certification as OptionRow[]) ?? []) as OptionRow[]).map(
                      (cert) => {
                        const id = Number(cert.id);
                        return (
                          <li key={id}>
                            <label>
                              <input
                                type="checkbox"
                                value={id}
                                className="certification"
                                name="ahu_certification_etl"
                                defaultChecked={savedCertIds.has(id)}
                                onChange={(e) => e.currentTarget.classList.toggle('changed')}
                              />
                              <span className="label">{String(cert.name ?? '')}</span>
                            </label>
                          </li>
                        );
                      }
                    )}
                  </ul>
                </div>
                {/* ★ One `<span>` PER saved certification and NOTHING when none
                    are saved — that is what the legacy multiCheckbox writes into
                    this element (multi_checkbox_selector.js:105-116 clears
                    innerHTML then concatenates "<span>" + outputSerializer(model)
                    + "</span>", so an empty collection leaves it empty). The port
                    printed a "None" placeholder legacy has no equivalent of, and
                    joined the names into a bare text node — which also missed
                    `.certification-group .checked-certifications span`
                    (common.css:20749, ahu/styles.css:2870, font-size 13px /
                    #212121) and double-spaced the separator, since the ", " is
                    CSS too (`span:not(:empty) + span:not(:empty):before` with
                    `margin-left:-0.25em`, common.css:20753 — which is why the
                    spans are emitted with no whitespace between them, as legacy's
                    string concatenation does). Same fix already shipped for
                    PlateHeatExchangerForm and PlateHeatExchangerSelector. */}
                <span className="checked-certifications output certification_output">
                  {(savedCerts ?? [])
                    .map((c) => [String(c.id), String(c.name ?? '')] as const)
                    .filter(([, name]) => Boolean(name))
                    .map(([cid, name]) => <span key={cid}>{name}</span>)}
                </span>
                <span className="label">Certification</span>
              </div>
            </li>
            <MInput api={api} name="height_from_floor" label="Height from Floor" unit="in" />
          </FormSubtab>

          <FormSubtab type="energy_wheel" cid={row.id} name="performance_summer" label="Performance : Summer" open={sub('performance_summer')} onToggle={api.toggleSubtab}>
            <StreamSubsection api={api} a="oa" b="sa" season="summer" streams={streams}
              enteringHeading="SA ENTERING" leavingHeading="SA LEAVING" spField="oa_sp" spLabel="SP Outside" />
            <StreamSubsection api={api} a="ra" b="ea" season="summer" streams={streams}
              enteringHeading="EA ENTERING" leavingHeading="EA LEAVING" spField="ra_sp" spLabel="SP Return" />
            <SeasonTotals api={api} season="summer" />
          </FormSubtab>

          <FormSubtab type="energy_wheel" cid={row.id} name="performance_winter" label="Performance : Winter" open={sub('performance_winter')} onToggle={api.toggleSubtab}>
            <StreamSubsection api={api} a="oa" b="sa" season="winter" streams={streams}
              enteringHeading="SA ENTERING" leavingHeading="SA LEAVING" spField="oa_sp" spLabel="SP Outside" />
            {/* no spField — the winter EA block has no SP Return row
                (tmpl:1240-1260); Condensate, EATR and OACF moved into
                StreamSubsection / SeasonTotals, where legacy keeps them. */}
            <StreamSubsection api={api} a="ra" b="ea" season="winter" streams={streams}
              enteringHeading="EA ENTERING" leavingHeading="EA LEAVING" />
            <SeasonTotals api={api} season="winter" />
          </FormSubtab>

          <FormSubtab type="energy_wheel" cid={row.id} name="pricing" label="Pricing" open={sub('pricing')} onToggle={api.toggleSubtab}>
            <PricingRows api={api} label="ER Wheel" />
          </FormSubtab>

          <BlankOffSlot api={api} sectionId={section.id} />
        </ul>
      </form>
      {/* components/energy_wheel/tmpl/form.tmpl — the footer note closes every legacy
          component form; this port dropped it (same omission as the coil
          panel this branch fixes). */}
      <RequiredFieldNote />
    </>
  );
};

export default EnergyWheelForm;
